import React, { useState, useEffect } from "react";
import EventDetails from "./EventDetails";
import ParticipantsList from "./ParticipantsList";
import DriversList from "./DriversList.js";
import RidesList from "./RidesList";
import RideTracker from "./RideTracker";
import ShuttleManager from "./ShuttleManager";
import useCRUD from "../../hooks/useCRUD";
import { useParams, useHistory } from "react-router-dom";
import Spinner from "../Spinner/Spinner";
import { formatDateWithDay } from "../../utils/dateUtils.js";
import EventActionHistory from "./EventActionHistory.js";
import ParticipantsMapModal from "./ParticipantsMapModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCarSide,
  faClipboardList,
  faMapLocationDot,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";

const ManageEventDashboard = () => {
  const { id } = useParams();

  const [eventDetails, setEventDetails] = useState(null);
  const { data: eventParticipationsData, fetchData: fetchEventParticipations } =
    useCRUD(`/api/event-participations/event/${id}`);

  const { fetchData: fetchEventParticipationsConfig } = useCRUD(
    `/api/events/${id}/create-participation`,
  );

  const { data: suggestionsData, fetchData: fetchSuggestions } = useCRUD(
    `/api/event-participations/auto-suggest/${id}`,
  );

  const [eventParticipations, setEventParticipations] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [configFetchAttempted, setConfigFetchAttempted] = useState(false);

  const [isEventExpired, setIsEventExpired] = useState(false);

  const [isMapOpen, setIsMapOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("manage");

  const history = useHistory();

  // Check if event is RSVP Only
  const isRSVPOnly = eventDetails?.eventSignUpType === "RSVP Only";
  const isShuttleEvent = eventDetails?.eventSignUpType === "Shuttle Event";
  const needsRide = (participation) =>
    participation?.needRide === true ||
    String(participation?.needRide || "")
      .trim()
      .toLowerCase() === "yes";
  const driverCount = eventParticipations.filter(
    (p) => p.isDriver && p.availability === "Yes",
  ).length;
  const ridersNeedingRide = eventParticipations.reduce(
    (total, p) =>
      !p.isDriver && needsRide(p) ? total + (p.noOfPpl || 1) : total,
    0,
  );
  const assignedRiders = eventParticipations.reduce(
    (total, p) =>
      !p.isDriver &&
      (isShuttleEvent ? p.shuttlePickupSpotId : p.assignedDriverId)
        ? total + (p.noOfPpl || 1)
        : total,
    0,
  );
  const confirmedParticipants = eventParticipations.filter(
    (p) => !p.isDriver && needsRide(p),
  ).length;
  const mapParticipants = eventParticipations.filter(
    (p) => !p.isDriver && needsRide(p),
  );

  const sectionLinks = [
    {
      id: "manage",
      label: isShuttleEvent
        ? "Shuttle Manager"
        : isRSVPOnly
          ? "Manage"
          : "Assign Rides",
    },
    !isRSVPOnly &&
      !isShuttleEvent && { id: "ride-tracker", label: "Ride Tracker" },
    { id: "timeline", label: "Timeline" },
  ].filter(Boolean);

  const refreshEventParticipations = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching event participations...");
      const fetchedEventParticipations = await fetchEventParticipations();

      console.log("Fetched event participations:", fetchedEventParticipations);
      if (fetchedEventParticipations) {
        // Make sure fetchedEventParticipations is not null

        console.log("Event participations:", fetchedEventParticipations);

        setEventDetails(fetchedEventParticipations);
        setEventParticipations(
          fetchedEventParticipations?.eventParticipations || [],
        );
      }
    } catch (error) {
      console.error("Error fetching event participations:", error);
    } finally {
      setIsLoading(false);
      //Reload the page
    }
  };

  const handleSendToGoup = () => {
    // Navigate to the manage event page
    history.push(`/group/${eventDetails?.participantGroup}`);
  };

  const manageConfigEventParticipations = async () => {
    if (!configFetchAttempted) {
      console.log("Fetching event participations config...");
      await fetchEventParticipationsConfig(); // Make sure this function awaits the fetch
      setConfigFetchAttempted(true); // Mark that the fetch has been attempted
      refreshEventParticipations(); // Optionally refresh participations after config fetch
    }
  };
  useEffect(() => {
    refreshEventParticipations();
    fetchSuggestions();
  }, [fetchEventParticipations]);

  useEffect(() => {
    if (eventDetails?.pingSchedule === null && !configFetchAttempted) {
      console.log("Configuring event participations...");
      manageConfigEventParticipations();
    }
  }, [eventDetails, configFetchAttempted]); // Depend on both eventDetails and configFetchAttempted

  // New useEffect for the additional trigger
  useEffect(() => {
    console.log("Checking if event has expired...");
    const initiateManageConfig = async () => {
      const now = new Date(); // This is in local time
      const nowUtc = new Date(now.toISOString()); // Convert now to UTC

      const pingScheduleDate = new Date(eventDetails?.pingSchedule + "Z"); // Appending 'Z' to indicate UTC
      const dateOfOccurrence = new Date(eventDetails?.dateOfOccurrence + "Z"); // Appending 'Z' to indicate UTC

      const dayAfterOccurrence = new Date(dateOfOccurrence);
      dayAfterOccurrence.setUTCDate(dayAfterOccurrence.getUTCDate() + 1); // Increment the day in UTC

      // Check if the current date is more than 24 hours after the date of occurrence
      const isEventExpired = nowUtc.getTime() > dayAfterOccurrence.getTime();

      if (isEventExpired) {
        console.log("Event has expired. Skipping configuration.");
        setIsEventExpired(true);
        return; // Exit the function if the event has expired
      }

      if (
        nowUtc > pingScheduleDate &&
        nowUtc < dayAfterOccurrence &&
        eventParticipations.length <
          (eventDetails?.eligibleGroupParticipantCount ??
            eventDetails?.participantGroupDetails?.noOfParticipants)
      ) {
        const isConfirmed = window.confirm(
          "You have added additional participants. Do you want to send RSVPs to them?",
        );
        if (isConfirmed) {
          await manageConfigEventParticipations();
        }
      }
    };

    if (eventDetails) {
      initiateManageConfig();
    }
  }, [eventDetails]); // Add dependencies as needed

  return (
    <div className="min-h-screen bg-gray-100 px-2 py-4 sm:px-4 sm:py-6">
      {isLoading && <Spinner />}

      <div className="mx-auto w-full max-w-7xl space-y-4 sm:space-y-5">
        <EventDetails
          event={eventDetails}
          isEventExpired={isEventExpired}
          refreshData={refreshEventParticipations}
        />

        {(eventParticipations.length > 0 || isShuttleEvent) && (
          <div className="sticky top-0 z-20 -mx-2 border-y border-gray-200 bg-white/95 px-2 py-2 shadow-sm backdrop-blur sm:static sm:mx-0 sm:rounded-xl sm:border sm:px-3 sm:shadow-sm">
            <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
              {sectionLinks.map((link) => (
                <button
                  type="button"
                  key={link.id}
                  onClick={() => setActiveTab(link.id)}
                  className={`whitespace-nowrap rounded-lg px-3 py-2 text-xs font-bold ${
                    activeTab === link.id
                      ? "bg-[#004a7e] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-[#eaf3fa] hover:text-[#004a7e]"
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isLoading &&
          eventDetails?.participantGroupDetails?.noOfParticipants < 1 && (
            <div className="alert alert-warning">
              This event doesn't have any participants or drivers. Add members
              by clicking the button.{" "}
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSendToGoup}
              >
                Manage Group Members
              </button>
            </div>
          )}

        {!isLoading &&
          eventParticipations.length === 0 &&
          (eventDetails?.pingParticipants === true ||
            eventDetails?.pingDrivers === true) && (
            <div className="alert alert-warning">
              This event is configured to ping participants or drivers for
              availability.<br></br> You can configure pickups after
              notifications go out on{" "}
              {formatDateWithDay(eventDetails?.pingSchedule)}.
              {/* To
            trigger notifications manually, click{" "}
            <button
              className="btn btn-primary btn-sm"
              onClick={manageConfigEventParticipations}
            >
              Send Reminders Now
            </button> */}
            </div>
          )}
        {eventParticipations.length > 0 && (
          <div className="space-y-4 sm:space-y-5">
            {activeTab === "manage" && (
              <section
                id="manage"
                className="scroll-mt-24 space-y-4 sm:space-y-5"
              >
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {isRSVPOnly && (
                    <DashboardStat
                      icon={faUserCheck}
                      label="Confirmed"
                      value={confirmedParticipants}
                      valueClass="text-green-700"
                    />
                  )}
                  {!isRSVPOnly && (
                    <DashboardStat
                      icon={faClipboardList}
                      label="Need rides"
                      value={ridersNeedingRide}
                      valueClass="text-blue-600"
                    />
                  )}
                  {!isRSVPOnly && (
                    <DashboardStat
                      icon={faCarSide}
                      label="Drivers"
                      value={driverCount}
                      valueClass="text-yellow-500"
                    />
                  )}
                  {!isRSVPOnly && (
                    <DashboardStat
                      icon={faUserCheck}
                      label={isShuttleEvent ? "At spots" : "Assigned"}
                      value={assignedRiders}
                      valueClass="text-green-700"
                    />
                  )}
                </div>

                {!isRSVPOnly && !isShuttleEvent && (
                  <div className="flex justify-end">
                    <button
                      className="rounded-lg border border-[#004a7e] bg-white px-3 py-2 text-sm font-bold text-[#004a7e] hover:bg-[#004a7e] hover:text-white"
                      onClick={() => setIsMapOpen(true)}
                    >
                      <FontAwesomeIcon
                        icon={faMapLocationDot}
                        className="mr-1"
                      />
                      View Participants on Map
                    </button>
                  </div>
                )}

                {isShuttleEvent ? (
                  <>
                    <ShuttleManager eventId={id} />
                    <DriversList
                      eventId={id}
                      eventParticipations={eventParticipations}
                      refreshData={refreshEventParticipations}
                      event={eventDetails}
                      isEventExpired={isEventExpired}
                    />
                  </>
                ) : (
                  <>
                    {!isRSVPOnly && !isEventExpired && (
                      <RidesList
                        eventId={id}
                        eventParticipations={eventParticipations}
                        refreshData={refreshEventParticipations}
                        event={eventDetails}
                        isEventExpired={isEventExpired}
                      />
                    )}

                    <ParticipantsList
                      eventId={id}
                      eventParticipations={eventParticipations}
                      refreshData={refreshEventParticipations}
                      event={eventDetails}
                      isEventExpired={isEventExpired}
                      suggestions={suggestionsData}
                    />

                    {!isRSVPOnly && (
                      <DriversList
                        eventId={id}
                        eventParticipations={eventParticipations}
                        refreshData={refreshEventParticipations}
                        event={eventDetails}
                        isEventExpired={isEventExpired}
                      />
                    )}
                  </>
                )}
              </section>
            )}

            {activeTab === "ride-tracker" && !isRSVPOnly && !isShuttleEvent && (
              <section id="ride-tracker" className="scroll-mt-24">
                <RideTracker eventParticipations={eventParticipations} />
              </section>
            )}

            {activeTab === "timeline" && (
              <section id="timeline" className="scroll-mt-24">
                <EventActionHistory eventId={id} />
              </section>
            )}
          </div>
        )}
      </div>

      <ParticipantsMapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        participants={mapParticipants}
      />
    </div>
  );
};

const DashboardStat = ({
  icon,
  label,
  value,
  valueClass = "text-gray-900",
}) => (
  <div className="rounded-lg border border-gray-100 bg-white p-2 shadow-sm sm:rounded-xl sm:p-4">
    <div className="flex flex-col items-center gap-1 text-center sm:flex-row sm:gap-3 sm:text-left">
      <div className="hidden h-10 w-10 items-center justify-center rounded-lg bg-[#eaf3fa] text-[#004a7e] sm:flex">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 sm:text-xs">
          {label}
        </p>
        <p className={`text-xl font-black sm:text-2xl ${valueClass}`}>
          {value}
        </p>
      </div>
    </div>
  </div>
);

export default ManageEventDashboard;
